import { useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/command'
import { t } from '@/shared/lib/i18n'
import { cn } from '@/shared/lib/utils'

export interface SearchableSelectOption {
  value: string
  label: string
  disabled?: boolean
}

interface Props {
  options: SearchableSelectOption[]
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  disabled?: boolean
  className?: string
  id?: string
  'aria-label'?: string
  'aria-required'?: boolean
}

export const SearchableSelect = ({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
  className,
  id,
  'aria-label': ariaLabel,
  'aria-required': ariaRequired,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const selectedOption = options.find((option) => option.value === value)

  const handleOpenChange = (nextOpen: boolean) => {
    // Clear search whenever the popover opens or closes so sentinel options like
    // "All users" stay selectable after a prior filtered pick (cmdk otherwise
    // keeps the old filter text across reopen).
    setSearch('')
    setOpen(nextOpen)
  }

  const handleSelect = (nextValue: string) => {
    onValueChange(nextValue)
    setSearch('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          aria-required={ariaRequired}
          disabled={disabled}
          className={cn(
            'border-input flex h-9 w-full cursor-pointer items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-all outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span
            title={selectedOption?.label}
            className={cn(
              'min-w-0 flex-1 truncate text-left',
              !selectedOption && 'text-muted-foreground',
            )}
          >
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-[8rem] p-0"
      >
        <Command
          filter={(value, search) => {
            // `value` is the composed "label\u0000optionValue" (see CommandItem below): search only
            // against the label portion so duplicate-label options stay independently addressable.
            const label = value.split('\u0000')[0]
            return label.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0
          }}
        >
          <CommandInput
            placeholder={searchPlaceholder ?? t('shared.searchableSelect.searchPlaceholder')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage ?? t('shared.searchableSelect.emptyMessage')}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  // Compose a unique cmdk value; without the optionValue suffix, options sharing a
                  // label (e.g. two users with the same name) collapse into one highlighted/selected
                  // row in cmdk. The label stays first for search (see filter above).
                  value={`${option.label}\u0000${option.value}`}
                  disabled={option.disabled}
                  onSelect={() => handleSelect(option.value)}
                >
                  <Check
                    className={cn(
                      'size-4 shrink-0',
                      option.value === value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span title={option.label} className="min-w-0 truncate">
                    {option.label}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
